import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import BoardModal, {
  BMCancelButton, BMPrimaryButton, BMDangerButton, BM_LABEL, BM_INPUT, BM_TITLE_INPUT, BM_TEXTAREA,
} from '../../../components/BoardModal';
import { getAccount } from '../../../auth/authService';
import { getProjectMembers, getMyMembership, ProjectMember, ProjectMemberRole } from '../../../services/projectMemberService';
import {
  FormDef, FormField, Submission, FieldInput, FormApprover,
  TARGET_LABEL, TargetEntityType, FieldType, FIELD_TYPE_LABEL, SUBMISSION_STATUS_LABEL, SubmissionStatus,
  getProjectForms, createForm, updateForm, deleteForm,
  getFormFields, createFormField, updateFormField, deleteFormField,
  getFormSubmissions, getFormSubmissionCount, FormSettingsInput,
  ATTACHMENT_TYPES, targetEntityLogicalName, getEntityFields, getEntityFieldOptions, EntityFieldMeta,
  getFormApprovers, addFormApprover, removeFormApprover,
  submitForm, approveSubmission, rejectSubmission, isSubmissionResolved,
} from '../../../services/formsService';

interface Props { pca: IPublicClientApplication; projectId: string }

const EXTERNAL_FORMS_MESSAGE =
  'External form submissions require Client Portal, which is coming in a future release. Internal forms (for people in your organization) are unaffected.';

// ── Role model ─────────────────────────────────────────────────────────
type FormsRole = 'pm' | 'tm' | 'stakeholder' | 'observer';
interface Me { oid: string; name: string; email: string | null; role: FormsRole }

function roleFromMember(m: ProjectMember | null): FormsRole {
  // No membership row → treat as PM (solo project owner), matching useCurrentUser.
  if (!m) return 'pm';
  switch (m.dagrun_pm_role) {
    case ProjectMemberRole.ProjectManager: return 'pm';
    case ProjectMemberRole.TeamMember:     return 'tm';
    case ProjectMemberRole.Stakeholder:    return 'stakeholder';
    case ProjectMemberRole.Observer:       return 'observer';
    default: return 'observer';
  }
}

export default function FormsTab({ pca, projectId }: Props) {
  const [forms, setForms] = useState<FormDef[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState<Me | null>(null);
  const [editing, setEditing] = useState<FormDef | 'new' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<FormDef | null>(null);
  const [filling, setFilling] = useState<FormDef | null>(null);
  const [previewing, setPreviewing] = useState<FormDef | null>(null);
  const [inboxFormId, setInboxFormId] = useState<string>('');

  useEffect(() => {
    (async () => {
      try {
        const acct = await getAccount(pca);
        const oid = acct?.localAccountId ?? acct?.homeAccountId?.split('.')[0] ?? '';
        const membership = await getMyMembership(pca, projectId, oid).catch(() => null);
        setMe({ oid, name: acct?.name ?? acct?.username ?? 'Me', email: acct?.username ?? null, role: roleFromMember(membership) });
      } catch { setMe({ oid: '', name: 'Me', email: null, role: 'observer' }); }
    })();
  }, [pca, projectId]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const f = await getProjectForms(pca, projectId);
      setForms(f);
      const entries = await Promise.all(f.map(async x => [x.dagrun_pm_formid, await getFormSubmissionCount(pca, x.dagrun_pm_formid).catch(() => 0)] as const));
      setCounts(Object.fromEntries(entries));
      setInboxFormId(prev => (prev && f.some(x => x.dagrun_pm_formid === prev)) ? prev : (f[0]?.dagrun_pm_formid ?? ''));
    } finally { setLoading(false); }
  }, [pca, projectId]);
  useEffect(() => { reload(); }, [reload]);

  const canBuild = me?.role === 'pm' || me?.role === 'tm';
  const canFill = me?.role === 'pm' || me?.role === 'tm' || me?.role === 'stakeholder';
  const canSubmissions = me?.role === 'pm' || me?.role === 'tm';
  const isObserver = me?.role === 'observer';

  return (
    <div style={{ padding: '24px 32px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Forms</h3>
        {canBuild && <button type="button" className="btn-primary" onClick={() => setEditing('new')}>+ New Form</button>}
      </div>

      {/* Forms list */}
      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        {loading || !me ? (
          <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>Loading…</div>
        ) : forms.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--ink-3)', padding: '8px 0' }}>
            No forms{canBuild ? ' — create one to collect requests from your team.' : ' yet.'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                <Th>Form</Th><Th>Creates</Th><Th>Review</Th><Th>Status</Th><Th>Submissions</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {forms.map(f => (
                <tr key={f.dagrun_pm_formid} style={{ borderBottom: '0.5px solid var(--border-light)' }}>
                  <Td><span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>{f.dagrun_pm_name}</span></Td>
                  <Td><span style={{ fontSize: 13, color: 'var(--ink-3)' }}>{f.dagrun_pm_targetentitytype != null ? TARGET_LABEL[f.dagrun_pm_targetentitytype] ?? '—' : '—'}</span></Td>
                  <Td>
                    <Badge bg={f.dagrun_pm_requiresreview === false ? '#F3F4F6' : '#FEF3C7'} fg={f.dagrun_pm_requiresreview === false ? '#5A6272' : '#92400E'}>
                      {f.dagrun_pm_requiresreview === false ? 'Immediate' : 'Reviewed'}
                    </Badge>
                  </Td>
                  <Td><Badge bg={f.dagrun_pm_isactive ? '#D1FAE5' : '#F3F4F6'} fg={f.dagrun_pm_isactive ? '#065F46' : '#9198A8'}>{f.dagrun_pm_isactive ? 'Active' : 'Inactive'}</Badge></Td>
                  <Td><span style={{ fontSize: 13, color: 'var(--ink-2)', fontFamily: 'var(--font-mono)' }}>{counts[f.dagrun_pm_formid] ?? 0}</span></Td>
                  <Td>
                    <div style={{ display: 'flex', gap: 10 }}>
                      {canFill && f.dagrun_pm_isactive !== false && <LinkBtn onClick={() => setFilling(f)}>Fill out</LinkBtn>}
                      {isObserver && <LinkBtn onClick={() => setPreviewing(f)}>View</LinkBtn>}
                      {canBuild && <LinkBtn onClick={() => setEditing(f)}>Edit</LinkBtn>}
                      {canBuild && <LinkBtn danger onClick={() => setConfirmDelete(f)}>Delete</LinkBtn>}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Submissions — PM/TM only */}
      {canSubmissions && (
        <div className="card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <span className="eyebrow">SUBMISSIONS</span>
            {forms.length > 0 && (
              <select value={inboxFormId} onChange={e => setInboxFormId(e.target.value)}
                style={{ fontSize: 12, padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 6, fontFamily: 'var(--font-ui)' }}>
                {forms.map(f => <option key={f.dagrun_pm_formid} value={f.dagrun_pm_formid}>{f.dagrun_pm_name}</option>)}
              </select>
            )}
          </div>
          {inboxFormId && me
            ? <SubmissionInbox pca={pca} projectId={projectId} me={me} form={forms.find(f => f.dagrun_pm_formid === inboxFormId)!} onChanged={reload} />
            : <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>No forms yet.</div>}
        </div>
      )}

      {editing && canBuild && (
        <FormBuilder pca={pca} projectId={projectId} form={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
      )}
      {filling && me && (
        <FillOutModal pca={pca} projectId={projectId} me={me} form={filling}
          onClose={() => setFilling(null)} onSubmitted={() => { setFilling(null); reload(); }} />
      )}
      {previewing && (
        <FillOutModal pca={pca} projectId={projectId} me={me!} form={previewing} readOnly
          onClose={() => setPreviewing(null)} onSubmitted={() => setPreviewing(null)} />
      )}
      {confirmDelete && canBuild && (
        <BoardModal open title="Delete form" onClose={() => setConfirmDelete(null)}
          footer={<>
            <BMCancelButton onClick={() => setConfirmDelete(null)} />
            <BMDangerButton onClick={() => deleteForm(pca, confirmDelete.dagrun_pm_formid).then(() => { setConfirmDelete(null); reload(); })}>Delete</BMDangerButton>
          </>}>
          Delete <strong style={{ color: '#0F1117' }}>{confirmDelete.dagrun_pm_name}</strong> and its fields? This cannot be undone.
        </BoardModal>
      )}
    </div>
  );
}

// ── Fill-out / preview modal ───────────────────────────────────────────
function FillOutModal({ pca, projectId, me, form, readOnly, onClose, onSubmitted }: {
  pca: IPublicClientApplication; projectId: string; me: Me; form: FormDef; readOnly?: boolean;
  onClose: () => void; onSubmitted: () => void;
}) {
  const [fields, setFields] = useState<FormField[] | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<null | { immediate: boolean }>(null);

  useEffect(() => {
    getFormFields(pca, form.dagrun_pm_formid).then(setFields).catch(() => setFields([]));
  }, [pca, form.dagrun_pm_formid]);

  const set = (name: string, v: string) => setValues(prev => ({ ...prev, [name]: v }));

  const missingRequired = useMemo(
    () => (fields ?? []).filter(f => f.dagrun_pm_isrequired && !(values[f.dagrun_pm_name] ?? '').trim()),
    [fields, values],
  );

  const submit = async () => {
    if (!fields || submitting) return;
    if (missingRequired.length > 0) { setError('Please complete all required fields.'); return; }
    setSubmitting(true); setError(null);
    try {
      const res = await submitForm(pca, {
        form, fields, projectId, data: values,
        submitterName: me.name, submitterEmail: me.email,
      });
      setDone({ immediate: !!res.created });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Submission failed.');
    } finally { setSubmitting(false); }
  };

  return (
    <BoardModal open title={readOnly ? `Preview — ${form.dagrun_pm_name}` : form.dagrun_pm_name} width={560} onClose={onClose}
      footer={done ? <BMCancelButton onClick={onClose}>Close</BMCancelButton> : (
        <>
          <BMCancelButton onClick={onClose} disabled={submitting} />
          {!readOnly && <BMPrimaryButton onClick={submit} disabled={submitting || !fields}>{submitting ? 'Submitting…' : 'Submit'}</BMPrimaryButton>}
        </>
      )}>
      {form.dagrun_pm_formdescription && <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 14 }}>{form.dagrun_pm_formdescription}</div>}
      {readOnly && <div style={{ fontSize: 12, color: 'var(--ink-4)', marginBottom: 12, fontStyle: 'italic' }}>Read-only preview — Observers cannot submit forms.</div>}

      {done ? (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: 34, marginBottom: 8 }}>✓</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>Submitted</div>
          <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 6 }}>
            {done.immediate
              ? `A ${TARGET_LABEL[form.dagrun_pm_targetentitytype ?? TargetEntityType.Task] ?? 'record'} was created from your submission.`
              : 'Your submission is pending review by an approver.'}
          </div>
        </div>
      ) : fields === null ? (
        <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>Loading…</div>
      ) : fields.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>This form has no fields yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {fields.map(f => (
            <FormFieldControl key={f.dagrun_pm_formfieldid} field={f} value={values[f.dagrun_pm_name] ?? ''}
              disabled={!!readOnly} onChange={v => set(f.dagrun_pm_name, v)} />
          ))}
          {error && <div style={{ fontSize: 12, color: '#DC2626' }}>{error}</div>}
        </div>
      )}
    </BoardModal>
  );
}

function FormFieldControl({ field, value, disabled, onChange }: {
  field: FormField; value: string; disabled: boolean; onChange: (v: string) => void;
}) {
  const label = (
    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 5 }}>
      {field.dagrun_pm_name}{field.dagrun_pm_isrequired && <span style={{ color: '#DC2626' }}> *</span>}
    </label>
  );
  const ph = field.dagrun_pm_placeholdertext ?? '';
  const common = { value, disabled, onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => onChange(e.target.value) };
  let control: React.ReactNode;
  switch (field.dagrun_pm_fieldtype) {
    case FieldType.LongText: control = <textarea {...common} placeholder={ph} rows={4} style={BM_TEXTAREA} />; break;
    case FieldType.Number:   control = <input type="number" {...common} placeholder={ph} style={BM_INPUT} />; break;
    case FieldType.Date:     control = <input type="date" {...common} style={BM_INPUT} />; break;
    case FieldType.Email:    control = <input type="email" {...common} placeholder={ph || 'name@example.com'} style={BM_INPUT} />; break;
    case FieldType.ChoiceSingle:
      control = (
        <select {...common} style={BM_INPUT}>
          <option value="">Select…</option>
          {optionList(parseOptions(field.dagrun_pm_choiceoptionsjson)).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ); break;
    case FieldType.FileAttachment:
      control = <input type="text" {...common} placeholder="File name / link" style={BM_INPUT} />; break;
    default: control = <input type="text" {...common} placeholder={ph} style={BM_INPUT} />; break;
  }
  return <div>{label}{control}</div>;
}

// ── Submissions inbox with review ─────────────────────────────────────
function SubmissionInbox({ pca, projectId, me, form, onChanged }: {
  pca: IPublicClientApplication; projectId: string; me: Me; form: FormDef; onChanged: () => void;
}) {
  const [subs, setSubs] = useState<Submission[]>([]);
  const [fields, setFields] = useState<FormField[]>([]);
  const [approverIds, setApproverIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [review, setReview] = useState<Submission | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [s, ff, ap] = await Promise.all([
        getFormSubmissions(pca, form.dagrun_pm_formid),
        getFormFields(pca, form.dagrun_pm_formid).catch(() => [] as FormField[]),
        getFormApprovers(pca, form.dagrun_pm_formid).catch(() => [] as FormApprover[]),
      ]);
      setSubs(s); setFields(ff);
      setApproverIds(new Set(ap.map(a => a.dagrun_pm_userid).filter(Boolean) as string[]));
    } finally { setLoading(false); }
  }, [pca, form.dagrun_pm_formid]);
  useEffect(() => { reload(); }, [reload]);

  // Eligible approver = PM (dynamic) OR explicitly-added approver for this form.
  const canReview = me.role === 'pm' || approverIds.has(me.oid);

  if (loading) return <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>Loading…</div>;
  if (subs.length === 0) return <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>No submissions yet.</div>;

  return (
    <>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
            <Th>Submitted</Th><Th>Submitter</Th><Th>Status</Th><Th></Th>
          </tr>
        </thead>
        <tbody>
          {subs.map(s => {
            const pending = s.dagrun_pm_status === SubmissionStatus.Pending;
            return (
              <tr key={s.dagrun_pm_submissionid} style={{ borderBottom: '0.5px solid var(--border-light)' }}>
                <Td><span style={{ fontSize: 12, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>{s.dagrun_pm_submittedon ? new Date(s.dagrun_pm_submittedon).toLocaleString() : '—'}</span></Td>
                <Td><span style={{ fontSize: 13, color: 'var(--ink)' }}>{s.dagrun_pm_submittername || s.dagrun_pm_submitteremail || 'Anonymous'}</span></Td>
                <Td><SubStatusBadge status={s.dagrun_pm_status ?? SubmissionStatus.Pending} /></Td>
                <Td>
                  <LinkBtn onClick={() => setReview(s)}>
                    {canReview && pending ? 'Review' : 'View'}
                  </LinkBtn>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {review && (
        <ReviewModal pca={pca} projectId={projectId} me={me} form={form} fields={fields}
          submission={review} canReview={canReview}
          onClose={() => setReview(null)}
          onResolved={() => { setReview(null); reload(); onChanged(); }} />
      )}
    </>
  );
}

function ReviewModal({ pca, projectId, me, form, fields, submission, canReview, onClose, onResolved }: {
  pca: IPublicClientApplication; projectId: string; me: Me; form: FormDef; fields: FormField[];
  submission: Submission; canReview: boolean; onClose: () => void; onResolved: () => void;
}) {
  let data: Record<string, unknown> = {};
  try { data = submission.dagrun_pm_submissiondatajson ? JSON.parse(submission.dagrun_pm_submissiondatajson) : {}; } catch { /* empty */ }
  const [rejecting, setRejecting] = useState(false);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const resolved = isSubmissionResolved(submission) || submission.dagrun_pm_status === SubmissionStatus.InReview;
  const canAct = canReview && submission.dagrun_pm_status === SubmissionStatus.Pending;

  const doApprove = async () => {
    setBusy(true); setMsg(null);
    try {
      const outcome = await approveSubmission(pca, { submission, form, fields, projectId, reviewerId: me.oid, reviewerName: me.name });
      if (outcome === 'conflict') { setMsg('This submission was already resolved by another approver.'); setTimeout(onResolved, 1200); return; }
      onResolved();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Approval failed.');
    } finally { setBusy(false); }
  };
  const doReject = async () => {
    if (!notes.trim()) { setMsg('A reason is required to reject.'); return; }
    setBusy(true); setMsg(null);
    try {
      const outcome = await rejectSubmission(pca, { submission, reviewerId: me.oid, reviewerName: me.name, notes: notes.trim() });
      if (outcome === 'conflict') { setMsg('This submission was already resolved by another approver.'); setTimeout(onResolved, 1200); return; }
      onResolved();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Rejection failed.');
    } finally { setBusy(false); }
  };

  const nameByField = new Map(fields.map(f => [f.dagrun_pm_name, f]));

  return (
    <BoardModal open title="Submission" width={560} onClose={onClose}
      footer={canAct ? (
        rejecting ? (
          <>
            <BMCancelButton onClick={() => { setRejecting(false); setMsg(null); }} disabled={busy} />
            <BMDangerButton onClick={doReject} disabled={busy || !notes.trim()}>Confirm reject</BMDangerButton>
          </>
        ) : (
          <>
            <BMCancelButton onClick={onClose} disabled={busy} />
            <button type="button" className="btn-secondary" disabled={busy} onClick={() => setRejecting(true)}
              style={{ color: '#DC2626', borderColor: '#DC2626' }}>Reject</button>
            <BMPrimaryButton onClick={doApprove} disabled={busy}>{busy ? 'Working…' : 'Approve'}</BMPrimaryButton>
          </>
        )
      ) : <BMCancelButton onClick={onClose}>Close</BMCancelButton>}>

      {/* Status banner for resolved / claimed submissions */}
      {resolved && (
        <div style={{ marginBottom: 12 }}>
          <SubStatusBadge status={submission.dagrun_pm_status ?? SubmissionStatus.Pending} />
          {submission.dagrun_pm_reviewedbyname && (
            <span style={{ fontSize: 12, color: 'var(--ink-4)', marginLeft: 8 }}>
              by {submission.dagrun_pm_reviewedbyname}{submission.dagrun_pm_reviewedon ? ` · ${new Date(submission.dagrun_pm_reviewedon).toLocaleString()}` : ''}
            </span>
          )}
          {submission.dagrun_pm_reviewernotes && (
            <div style={{ fontSize: 13, color: '#991B1B', marginTop: 6 }}>Reason: {submission.dagrun_pm_reviewernotes}</div>
          )}
        </div>
      )}

      {/* Submitted answers */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {Object.keys(data).length === 0 && <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>No data captured.</div>}
        {Object.entries(data).map(([k, v]) => {
          const f = nameByField.get(k);
          return (
            <div key={k}>
              <div style={{ fontSize: 11, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {k}{f?.dagrun_pm_mapstofield ? ` → ${f.dagrun_pm_mapstofield}` : ''}
              </div>
              <div style={{ fontSize: 14, color: 'var(--ink)', whiteSpace: 'pre-wrap' }}>{v == null || v === '' ? '—' : String(v)}</div>
            </div>
          );
        })}
      </div>

      {rejecting && (
        <div style={{ marginTop: 14 }}>
          <label style={BM_LABEL}>Reason for rejection *</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} style={BM_TEXTAREA} placeholder="Visible to the submitter…" />
        </div>
      )}
      {!canReview && submission.dagrun_pm_status === SubmissionStatus.Pending && (
        <div style={{ marginTop: 12, fontSize: 12, color: 'var(--ink-4)' }}>You are not an approver for this form.</div>
      )}
      {msg && <div style={{ marginTop: 12, fontSize: 12, color: 'var(--danger)' }}>{msg}</div>}
    </BoardModal>
  );
}

function SubStatusBadge({ status }: { status: number }) {
  const map: Record<number, { bg: string; fg: string }> = {
    [SubmissionStatus.Pending]:  { bg: '#FEF3C7', fg: '#92400E' },
    [SubmissionStatus.InReview]: { bg: '#D4EEF5', fg: '#0A5568' },
    [SubmissionStatus.Approved]: { bg: '#D1FAE5', fg: '#065F46' },
    [SubmissionStatus.Rejected]: { bg: '#FEE2E2', fg: '#991B1B' },
    [SubmissionStatus.Complete]: { bg: '#D1FAE5', fg: '#065F46' },
  };
  const c = map[status] ?? { bg: '#F3F4F6', fg: '#5A6272' };
  return <Badge bg={c.bg} fg={c.fg}>{SUBMISSION_STATUS_LABEL[status] ?? '—'}</Badge>;
}

// ── Form builder drawer ───────────────────────────────────────────────
interface FieldDraft { key: string; id?: string; label: string; fieldType: number; required: boolean; options: string; mapsToField?: string | null }
let nextKey = 1;

function FormBuilder({ pca, projectId, form, onClose, onSaved }: {
  pca: IPublicClientApplication; projectId: string; form: FormDef | null;
  onClose: () => void; onSaved: () => void;
}) {
  const [name, setName] = useState(form?.dagrun_pm_name ?? '');
  const [description, setDescription] = useState(form?.dagrun_pm_formdescription ?? '');
  const [target, setTarget] = useState<number>(form?.dagrun_pm_targetentitytype ?? TargetEntityType.Task);
  const [isActive, setIsActive] = useState<boolean>(form?.dagrun_pm_isactive ?? true);
  const [sendConfirm, setSendConfirm] = useState<boolean>(form?.dagrun_pm_sendconfirmationemail ?? false);
  const [requiresReview, setRequiresReview] = useState<boolean>(form?.dagrun_pm_requiresreview ?? true);
  const [attMax, setAttMax] = useState<number>(form?.dagrun_pm_attachmentmaxmb ?? 10);

  const [fields, setFields] = useState<FieldDraft[]>([]);
  const [originalFieldIds, setOriginalFieldIds] = useState<string[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!form) { setFields([]); return; }
    getFormFields(pca, form.dagrun_pm_formid).then(fs => {
      setFields(fs.map(f => ({
        key: `k${nextKey++}`, id: f.dagrun_pm_formfieldid, label: f.dagrun_pm_name,
        fieldType: f.dagrun_pm_fieldtype, required: !!f.dagrun_pm_isrequired,
        options: parseOptions(f.dagrun_pm_choiceoptionsjson), mapsToField: f.dagrun_pm_mapstofield ?? null,
      })));
      setOriginalFieldIds(fs.map(f => f.dagrun_pm_formfieldid));
    }).catch(() => {});
  }, [pca, form]);

  const entityName = targetEntityLogicalName(target);

  const addEntityField = async (meta: EntityFieldMeta) => {
    setPickerOpen(false);
    let options = '';
    if (meta.fieldType === FieldType.ChoiceSingle) {
      options = (await getEntityFieldOptions(pca, entityName, meta.logicalName, meta.attributeType)).join(', ');
    }
    setFields(prev => [...prev, {
      key: `k${nextKey++}`, label: meta.displayName, fieldType: meta.fieldType,
      required: false, options, mapsToField: meta.logicalName,
    }]);
  };
  const updateField = (key: string, patch: Partial<FieldDraft>) => setFields(prev => prev.map(f => f.key === key ? { ...f, ...patch } : f));
  const removeField = (key: string) => setFields(prev => prev.filter(f => f.key !== key));

  const onDragEnd = (r: DropResult) => {
    if (!r.destination || r.source.index === r.destination.index) return;
    setFields(prev => {
      const next = [...prev];
      const [m] = next.splice(r.source.index, 1);
      next.splice(r.destination!.index, 0, m);
      return next;
    });
  };

  const submit = async () => {
    if (!name.trim() || saving) return;
    setSaving(true); setError(null);
    try {
      const settings: FormSettingsInput = {
        name: name.trim(), description: description.trim() || null, isPublic: false, isActive,
        targetEntityType: target, sendConfirmationEmail: sendConfirm, requiresReview,
        ratePerHour: null, attachmentTypes: ATTACHMENT_TYPES, attachmentMaxMb: attMax || null,
      };
      const formId = form ? (await updateForm(pca, form.dagrun_pm_formid, settings), form.dagrun_pm_formid) : await createForm(pca, projectId, settings);

      const keptIds = new Set(fields.filter(f => f.id).map(f => f.id!));
      for (const id of originalFieldIds) if (!keptIds.has(id)) await deleteFormField(pca, id);
      for (let i = 0; i < fields.length; i++) {
        const f = fields[i];
        if (!f.label.trim()) continue;
        const input: FieldInput = {
          label: f.label.trim(), fieldType: f.fieldType, required: f.required, sortOrder: i,
          choiceOptions: f.fieldType === FieldType.ChoiceSingle ? JSON.stringify(optionList(f.options)) : null,
          mapsToField: f.mapsToField ?? null,
        };
        if (f.id) await updateFormField(pca, f.id, input);
        else await createFormField(pca, formId, input);
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <BoardModal open title={form ? 'Edit Form' : 'New Form'} width={600} onClose={onClose}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !name.trim()}>{saving ? 'Saving…' : 'Save Form'}</BMPrimaryButton>
      </>}>
      <div className="eyebrow" style={{ marginBottom: 10 }}>FORM SETTINGS</div>
      <div style={{ marginBottom: 12 }}>
        <label style={BM_LABEL}>Form Name *</label>
        <input autoFocus value={name} onChange={e => setName(e.target.value)} style={BM_TITLE_INPUT} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={BM_LABEL}>Description</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} style={BM_TEXTAREA} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={BM_LABEL}>Creates (Target Entity)</label>
          <select value={target} onChange={e => setTarget(Number(e.target.value))} style={BM_INPUT}>
            {Object.entries(TARGET_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label style={BM_LABEL}>Attachment max (MB)</label>
          <input type="number" min={0} value={attMax} onChange={e => setAttMax(Number(e.target.value) || 0)} style={BM_INPUT} />
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        <Toggle label="Active" checked={isActive} onChange={setIsActive} />
        <Toggle label="Requires review before creating the record" checked={requiresReview} onChange={setRequiresReview} />
        <Toggle label="Send confirmation email on submit" checked={sendConfirm} onChange={setSendConfirm} />
        <div style={{ display: 'flex', gap: 8, background: 'var(--surface-2)', border: '1px solid var(--border-light)', borderRadius: 7, padding: '10px 12px', marginTop: 4 }}>
          <span style={{ fontSize: 14 }} aria-hidden>🔒</span>
          <span style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.5 }}>{EXTERNAL_FORMS_MESSAGE}</span>
        </div>
      </div>

      {/* Approvers — only meaningful for reviewed forms on an existing form. */}
      {form && requiresReview && (
        <ApproversSection pca={pca} projectId={projectId} form={form} target={target} />
      )}
      {!form && requiresReview && (
        <div style={{ fontSize: 12, color: 'var(--ink-4)', marginBottom: 12 }}>
          Save the form first, then add approvers. Project managers can always approve.
        </div>
      )}

      <div className="eyebrow" style={{ margin: '18px 0 10px' }}>FORM FIELDS</div>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="fields">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {fields.map((f, index) => (
                <Draggable key={f.key} draggableId={f.key} index={index}>
                  {(dp) => (
                    <div ref={dp.innerRef} {...dp.draggableProps}
                      style={{ ...dp.draggableProps.style, border: '1px solid var(--border-light)', borderRadius: 7, padding: 10, marginBottom: 8, background: '#fff' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span {...dp.dragHandleProps} title="Drag to reorder" style={{ cursor: 'grab', color: 'var(--ink-5)', fontSize: 14 }}>⋮⋮</span>
                        <input value={f.label} onChange={e => updateField(f.key, { label: e.target.value })} placeholder="Display label"
                          style={{ ...BM_INPUT, flex: 1 }} />
                        <span style={{ fontSize: 11, color: 'var(--ink-3)', background: 'var(--surface-2)', borderRadius: 4, padding: '4px 8px', whiteSpace: 'nowrap' }}>
                          {FIELD_TYPE_LABEL[f.fieldType] ?? '—'}
                        </span>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--ink-3)' }}>
                          <input type="checkbox" checked={f.required} onChange={e => updateField(f.key, { required: e.target.checked })} /> Req
                        </label>
                        <button type="button" onClick={() => removeField(f.key)} aria-label="Remove field"
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-4)', fontSize: 16 }}>×</button>
                      </div>
                      {f.mapsToField && (
                        <div style={{ fontSize: 10, color: 'var(--ink-5)', fontFamily: 'var(--font-mono)', marginTop: 4, marginLeft: 22 }}>
                          maps to {f.mapsToField}
                        </div>
                      )}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <button type="button" onClick={() => setPickerOpen(true)}
        style={{ background: 'transparent', color: '#0E7490', border: '1px solid #0E7490', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
        + Add Field
      </button>
      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}

      {pickerOpen && (
        <FieldPickerModal pca={pca} entityName={entityName}
          existing={new Set(fields.map(f => f.mapsToField).filter(Boolean) as string[])}
          onClose={() => setPickerOpen(false)} onPick={addEntityField} />
      )}
    </BoardModal>
  );
}

// ── Approvers management (per form) ────────────────────────────────────
function ApproversSection({ pca, projectId, form, target }: {
  pca: IPublicClientApplication; projectId: string; form: FormDef; target: number;
}) {
  const [approvers, setApprovers] = useState<FormApprover[]>([]);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false);

  const isCR = target === TargetEntityType.ChangeRequest;

  const reload = useCallback(async () => {
    const [ap, mem] = await Promise.all([
      getFormApprovers(pca, form.dagrun_pm_formid).catch(() => [] as FormApprover[]),
      getProjectMembers(pca, projectId).catch(() => [] as ProjectMember[]),
    ]);
    setApprovers(ap); setMembers(mem);
  }, [pca, projectId, form.dagrun_pm_formid]);
  useEffect(() => { reload(); }, [reload]);

  const approverOids = new Set(approvers.map(a => a.dagrun_pm_userid).filter(Boolean) as string[]);
  // Change Request forms: only PM-role members may be added (system rule that
  // only PMs approve CRs). Other forms: any project member.
  const addable = members.filter(m =>
    m.dagrun_pm_userid &&
    !approverOids.has(m.dagrun_pm_userid) &&
    (isCR ? m.dagrun_pm_role === ProjectMemberRole.ProjectManager : true));

  const add = async () => {
    const m = members.find(x => x.dagrun_pm_userid === pick);
    if (!m || !m.dagrun_pm_userid || busy) return;
    setBusy(true);
    try {
      const acct = await getAccount(pca);
      const oid = acct?.localAccountId ?? acct?.homeAccountId?.split('.')[0] ?? '';
      await addFormApprover(pca, {
        formId: form.dagrun_pm_formid, userId: m.dagrun_pm_userid,
        userName: m.dagrun_pm_username ?? 'Approver', userEmail: m.dagrun_pm_useremail,
        addedBy: oid, addedByName: acct?.name ?? 'PM',
      });
      setPick(''); await reload();
    } finally { setBusy(false); }
  };
  const remove = async (id: string) => {
    setBusy(true);
    try { await removeFormApprover(pca, id); setApprovers(prev => prev.filter(a => a.dagrun_pm_formapproverid !== id)); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ border: '0.5px solid var(--border-light)', borderRadius: 8, padding: 12, marginBottom: 12, background: 'var(--surface-2)' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>APPROVERS</div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 10 }}>
        Project managers can always approve. {isCR
          ? 'Change Request forms can only be approved by project managers.'
          : 'Add additional approvers below. Any one approver’s action resolves the submission.'}
      </div>
      {approvers.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          {approvers.map(a => (
            <span key={a.dagrun_pm_formapproverid} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', border: '0.5px solid var(--border-light)', borderRadius: 999, padding: '3px 6px 3px 12px', fontSize: 12 }}>
              {a.dagrun_pm_username}
              <button type="button" onClick={() => remove(a.dagrun_pm_formapproverid)} aria-label="Remove approver"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-4)', fontSize: 14 }}>×</button>
            </span>
          ))}
        </div>
      )}
      {!isCR && (
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={pick} onChange={e => setPick(e.target.value)} style={{ ...BM_INPUT, flex: 1 }}>
            <option value="">Add an approver…</option>
            {addable.map(m => <option key={m.dagrun_pm_projectmemberid} value={m.dagrun_pm_userid ?? ''}>{m.dagrun_pm_username}{m.dagrun_pm_role === ProjectMemberRole.ProjectManager ? ' (PM)' : ''}</option>)}
          </select>
          <button type="button" className="btn-secondary" onClick={add} disabled={busy || !pick}>Add</button>
        </div>
      )}
    </div>
  );
}

// ── Entity field picker ───────────────────────────────────────────────
function FieldPickerModal({ pca, entityName, existing, onClose, onPick }: {
  pca: IPublicClientApplication; entityName: string; existing: Set<string>;
  onClose: () => void; onPick: (meta: EntityFieldMeta) => void;
}) {
  const [all, setAll] = useState<EntityFieldMeta[] | null>(null);
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getEntityFields(pca, entityName)
      .then(fs => { if (!cancelled) setAll(fs); })
      .catch(() => { if (!cancelled) { setAll([]); setError('Could not load fields for this entity.'); } });
    return () => { cancelled = true; };
  }, [pca, entityName]);

  const filtered = (all ?? []).filter(f =>
    !q.trim() || f.displayName.toLowerCase().includes(q.trim().toLowerCase()) || f.logicalName.includes(q.trim().toLowerCase()));

  return (
    <BoardModal open title="Add a field" width={520} onClose={onClose}
      footer={<BMCancelButton onClick={onClose}>Close</BMCancelButton>}>
      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 10 }}>
        Fields from <strong>{entityName}</strong>. Submissions map to these entity columns.
      </div>
      <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search fields…" style={BM_INPUT} />
      {error && <div style={{ marginTop: 10, color: '#DC2626', fontSize: 12 }}>{error}</div>}
      <div style={{ marginTop: 10, maxHeight: 320, overflowY: 'auto' }}>
        {all === null ? (
          <div style={{ padding: 12, color: 'var(--ink-4)', fontSize: 13 }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 12, color: 'var(--ink-4)', fontSize: 13 }}>No matching fields.</div>
        ) : filtered.map(f => {
          const added = existing.has(f.logicalName);
          return (
            <button key={f.logicalName} type="button" disabled={added} onClick={() => onPick(f)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', textAlign: 'left', padding: '9px 10px', border: 'none', borderBottom: '0.5px solid #EAECF0', background: 'transparent', cursor: added ? 'default' : 'pointer', opacity: added ? 0.5 : 1 }}
              onMouseEnter={e => { if (!added) e.currentTarget.style.background = '#F5F6F8'; }}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
              <span>
                <span style={{ fontSize: 14, color: 'var(--ink)' }}>{f.displayName}</span>
                <span style={{ fontSize: 11, color: 'var(--ink-5)', fontFamily: 'var(--font-mono)', marginLeft: 8 }}>{f.logicalName}</span>
              </span>
              <span style={{ fontSize: 11, color: 'var(--ink-3)', background: 'var(--surface-2)', borderRadius: 4, padding: '2px 7px' }}>
                {added ? 'Added' : (FIELD_TYPE_LABEL[f.fieldType] ?? '—')}
              </span>
            </button>
          );
        })}
      </div>
    </BoardModal>
  );
}

// ── helpers ───────────────────────────────────────────────────────────
export function optionList(csv: string): string[] {
  return csv.split(',').map(s => s.trim()).filter(Boolean);
}
function parseOptions(json?: string | null): string {
  if (!json) return '';
  try { const a = JSON.parse(json); return Array.isArray(a) ? a.join(', ') : ''; } catch { return ''; }
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--ink-2)', cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} /> {label}
    </label>
  );
}
function Badge({ bg, fg, children }: { bg: string; fg: string; children: React.ReactNode }) {
  return <span style={{ background: bg, color: fg, fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10 }}>{children}</span>;
}
const Th = ({ children }: { children?: React.ReactNode }) => (
  <th style={{ padding: '8px 10px', fontWeight: 500, fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{children}</th>
);
const Td = ({ children }: { children: React.ReactNode }) => (
  <td style={{ padding: '9px 10px', verticalAlign: 'middle' }}>{children}</td>
);
function LinkBtn({ children, onClick, danger }: { children: React.ReactNode; onClick: () => void; danger?: boolean }) {
  return (
    <button type="button" onClick={onClick}
      style={{ background: 'transparent', border: 'none', padding: 0, color: danger ? '#DC2626' : '#0E7490', fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{children}</button>
  );
}
